/**
 * Oturumun ölüm anı — uygulamadaki TEK çıkış kapısı.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden tek yer
 * ═══════════════════════════════════════════════════════════════════════════
 * "Oturum bitti" kararı üç ayrı yerden gelebiliyor: bir API çağrısı 401
 * aldı, yoklama 401 aldı, ya da kullanıcı yeterince uzun süre hiçbir şeye
 * dokunmadı. Üçü de ayrı ayrı yönlendirme yapsaydı üç ayrı davranış olurdu
 * ve biri eksik kalırdı — nitekim eskiden öyleydi: istek katmanı hiç
 * yönlendirmiyor, ekranda "Not Found" yazısı kalıyordu.
 *
 * ---------------------------------------------------------------------------
 * 🔴 Neden `router.push` değil TAM SAYFA gezinmesi
 * ---------------------------------------------------------------------------
 * `router.push` React ağacını ayakta bırakıyor: uçuştaki `fetch`'ler,
 * zamanlayıcılar, yoklama döngüleri, açık SSE akışları çalışmaya devam eder
 * ve hepsi ölü bir oturumla istek atmayı sürdürür. `location.assign` süreci
 * bitiriyor — sayfa baştan kuruluyor, hiçbir şey hayatta kalmıyor.
 *
 * Hedef doğrudan `/login` DEĞİL, çerezleri silen route handler: ölü çerez
 * tarayıcıda kalırsa rota koruması "oturum var" sayıp kullanıcıyı panele geri
 * atar ve giriş sayfasına hiç ulaşılamaz (yaşanmış hata).
 *
 * ---------------------------------------------------------------------------
 * Sekmeler arası
 * ---------------------------------------------------------------------------
 * Kullanıcının beş sekmesi açıksa beşi de düşmeli. `BroadcastChannel` ile
 * haber veriliyor; haberi alan sekme YENİDEN yayınlamıyor (yoksa sonsuz
 * yankı olurdu), yalnızca kendi kapısına yürüyor.
 */

export type SessionScope = 'app' | 'backstage';

export type SessionEndReason =
  /// Sunucu oturumu tanımadı (süre doldu, iptal edildi, jeton yeniden kullanıldı).
  | 'expired'
  /// Kullanıcı uzun süre hiçbir şeye dokunmadı.
  | 'idle'
  /// Kullanıcı kendi bastı.
  | 'signout';

const CHANNEL = 'mentorivo-session';

/**
 * Hareketsizlik sınırı — 30 dakika.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden sunucuda değil burada
 * ═══════════════════════════════════════════════════════════════════════════
 * Sunucu "kullanıcı ekranın başında mı" sorusunu göremiyor; yalnızca istek
 * görüyor. Panelde açık duran bir sekme kendi başına istek atıyor (yoklama,
 * iş durumu, bildirim sayacı) — sunucu bunları kullanıcı etkinliği sansaydı
 * hareketsizlik sınırı HİÇ dolmazdı. Gerçek etkinliği (fare, klavye, dokunma)
 * yalnızca tarayıcı biliyor.
 *
 * Bu, "kozmetik güvenlik" değil: süre dolduğunda oturum SUNUCUDA da
 * kapatılıyor (`/api/session/end` refresh jetonunu iptal ediyor). Yani
 * bilgisayarın başından kalkan kullanıcının oturumu gerçekten ölüyor,
 * yalnızca ekran kilitlenmiş olmuyor.
 *
 * Tehdit modeli: açık bırakılmış, kilitlenmemiş bilgisayar. Çalınmış çereze
 * karşı koruma bu değil — o, sunucudaki mutlak ömür, jeton rotasyonu ve
 * yeniden kullanım tespitiyle sağlanıyor.
 */
export const IDLE_LIMIT_MS = 30 * 60 * 1000;

/** Panel oturumu ZATEN kısa (sunucuda 30 dk sabit); ekran biraz erken davranıyor. */
export const BACKSTAGE_IDLE_LIMIT_MS = 15 * 60 * 1000;

let ending = false;
let channel: BroadcastChannel | null = null;

function bus(): BroadcastChannel | null {
  if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') return null;
  channel ??= new BroadcastChannel(CHANNEL);
  return channel;
}

/** Ürün tarafının çıkış kapısı. Panelinki ÇAĞIRAN tarafından veriliyor. */
const APP_END_PATH = '/api/session/end';

function endpointFor(
  scope: SessionScope,
  reason: SessionEndReason,
  endpoint: string | undefined,
): string {
  const params = new URLSearchParams({ reason });

  /**
   * 🔴 Panelin adresi BU DOSYADA yazılı DEĞİL — ve bu bir titizlik değil,
   * paketleme meselesi.
   *
   * Bu modül ürün paketine giriyor (panel kabuğu da kullanıyor ama ürün
   * kabuğu da). Panelin yolunu buraya yazsaydık, mentorivo.com'u açan
   * herkesin indirdiği JavaScript'te "backstage diye bir yer var" ipucu
   * dururdu. Aynı gerekçeyle `lib/backstage.ts` de `NEXT_PUBLIC_`
   * kullanmıyor.
   *
   * Panel tarafı kendi yolunu `endpoint` ile veriyor; o değer yalnızca
   * panel paketine giren dosyalarda yazılı.
   */
  if (scope === 'backstage') return `${endpoint ?? APP_END_PATH}?${params.toString()}`;

  /* Giriş sonrası kullanıcıyı bıraktığı yere döndürmek için. Panel dışına
     çıkan bir adres yazılmıyor — `next` yalnızca kendi yolumuz olabilir,
     yoksa açık yönlendirme (open redirect) kapısı olurdu; route handler
     ayrıca doğruluyor. */
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/')) {
    params.set('next', window.location.pathname + window.location.search);
  }
  return `${APP_END_PATH}?${params.toString()}`;
}

/**
 * Oturumu bitirir ve kullanıcıyı giriş ekranına götürür.
 *
 * Yeniden girilebilir DEĞİL: ilk çağrı bayrağı kaldırıyor ve sonrakiler
 * sessizce dönüyor. On paralel istek aynı anda 401 aldığında on yönlendirme
 * başlatılmamalı.
 */
export function endSession(
  reason: SessionEndReason,
  scope: SessionScope = 'app',
  options: { broadcast?: boolean; endpoint?: string } = {},
): void {
  if (typeof window === 'undefined' || ending) return;
  ending = true;

  if (options.broadcast !== false) {
    try {
      bus()?.postMessage({
        type: 'end',
        reason,
        scope,
        endpoint: options.endpoint,
      } satisfies SessionSignal);
    } catch {
      // Kanal yoksa (eski tarayıcı, kısıtlı bağlam) tek sekme düşer — kabul.
    }
  }

  window.location.assign(endpointFor(scope, reason, options.endpoint));
}

type SessionSignal = {
  type: 'end';
  reason: SessionEndReason;
  scope: SessionScope;
  /** Panel sekmeleri kendi çıkış yolunu taşıyor — gerekçe `endpointFor`. */
  endpoint?: string;
};

/**
 * Diğer sekmelerin "oturum bitti" haberini dinler.
 *
 * Haberi alan sekme YENİDEN yayınlamıyor (`broadcast: false`) — aksi hâlde
 * her sekme diğerini tetikler ve kanal kendi yankısıyla dolardı.
 */
export function listenForSessionEnd(scope: SessionScope): () => void {
  const target = bus();
  if (!target) return () => undefined;

  const onMessage = (event: MessageEvent<SessionSignal>) => {
    if (event.data?.type !== 'end') return;
    // Ürün ve panel oturumları BAĞIMSIZ: biri düşünce diğeri düşmemeli.
    if (event.data.scope !== scope) return;
    endSession(event.data.reason, scope, {
      broadcast: false,
      endpoint: event.data.endpoint,
    });
  };

  target.addEventListener('message', onMessage);
  return () => target.removeEventListener('message', onMessage);
}

/** Yönlendirme başlamış mı — istek katmanı boşuna iş yapmasın diye. */
export function sessionEnding(): boolean {
  return ending;
}
